import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  IconButton,
  Collapse,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  ListItemText,
  Card,
  CardContent,
  Button,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight, FileDownload } from '@mui/icons-material';
import * as XLSX from 'xlsx';

interface IncidentData {
  id: string;
  application_id: string | null;
  configuration_item: string;
  short_description: string;
  resolved: string | null;
  opened: string;
  priority: string;
  brand: string | null;
  mvc: boolean | null;
  platform: string;
  portfolio: string;
  caused_by_platform: string;
  caused_by_portfolio: string;
  caused_by_change: boolean | null;
  change_type: string | null;
  change_process: string | null;
  triggering_change_request: string[];
  observations: string | null;
  root_cause_code: string | null;
  root_cause_sub_code: string | null;
  ttr_seconds: number | null;
  ttd_seconds: number | null;
}

interface BrandData {
  p1p2: number;
  p1p2_change: number;
  pct: string;
}

interface BrandMonthSummary {
  month: string;
  brands: Map<string, BrandData>; // Brand name -> BrandData
  total_p1p2: number;
  total_p1p2_change: number;
  total_pct: string;
}

interface PortfolioSummary {
  portfolio: string;
  p1p2_count: number;
  p1p2_change_count: number;
  pct: string;
  platforms?: Map<string, { p1p2_count: number; p1p2_change_count: number; pct: string }>;
}

interface OutageData {
  outage_number: string;
  configuration_item: string;
  outage_type: string;
  start_time: string;
  end_time: string;
  duration: string;
  task_number: string;
}

interface P1P2ChangeIncidentsProps {
  incidents: IncidentData[];
  outageData: OutageData[];
  loading: boolean;
  error: string | null;
  onRefresh: () => Promise<void>;
}

const P1P2ChangeIncidents: React.FC<P1P2ChangeIncidentsProps> = ({
  incidents,
  outageData,
  loading,
  error,
  onRefresh
}) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [brandMonthData, setBrandMonthData] = useState<BrandMonthSummary[]>([]);
  const [impactedPortfolioData, setImpactedPortfolioData] = useState<PortfolioSummary[]>([]);
  const [causedByPortfolioData, setCausedByPortfolioData] = useState<PortfolioSummary[]>([]);
  const [expandedPortfolios, setExpandedPortfolios] = useState<Set<string>>(new Set());
  const [expandedCausedByPortfolios, setExpandedCausedByPortfolios] = useState<Set<string>>(new Set());

  // Filter states
  const [timeRange, setTimeRange] = useState<string>('ytd');
  const [startDate, setStartDate] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-01-01`;
  });
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [mvcFilter, setMvcFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const [impactedPortfolioFilter, setImpactedPortfolioFilter] = useState<string[]>([]);
  const [impactedPlatformFilter, setImpactedPlatformFilter] = useState<string[]>([]);
  const [causedByPlatformFilter, setCausedByPlatformFilter] = useState<string[]>([]);
  const [causedByPortfolioFilter, setCausedByPortfolioFilter] = useState<string[]>([]);
  const [showOnlyCausedByChange, setShowOnlyCausedByChange] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<string>('monthly');
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);

  // Sorting states
  type SortableColumns = 'opened' | 'resolved' | 'id' | 'priority' | 'application_id' | 'configuration_item' | 'brand' | 'short_description' | 'caused_by_change' | 'change_type' | 'triggering_change_request' | 'mttr' | 'mttd' | 'downtime';
  const [orderBy, setOrderBy] = useState<SortableColumns>('resolved');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Format date to dd-mm-yyyy
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get ISO week number (ISO 8601 - weeks start on Monday)
  const getISOWeekNumber = (date: Date): { week: number; year: number } => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7; // Make Sunday = 7 instead of 0
    d.setUTCDate(d.getUTCDate() + 4 - dayNum); // Set to nearest Thursday (current week)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return { week: weekNo, year: d.getUTCFullYear() };
  };

  // Format seconds to readable time format
  const formatSeconds = (seconds: number | null): string => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Handle sorting
  const handleRequestSort = (property: SortableColumns) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort comparator function
  const getComparator = (order: 'asc' | 'desc', orderBy: SortableColumns) => {
    return (a: IncidentData, b: IncidentData) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case 'opened':
          aValue = a.opened ? new Date(a.opened).getTime() : 0;
          bValue = b.opened ? new Date(b.opened).getTime() : 0;
          break;
        case 'resolved':
          aValue = a.resolved ? new Date(a.resolved).getTime() : 0;
          bValue = b.resolved ? new Date(b.resolved).getTime() : 0;
          break;
        case 'id':
          aValue = a.id || '';
          bValue = b.id || '';
          break;
        case 'priority':
          aValue = a.priority || '';
          bValue = b.priority || '';
          break;
        case 'application_id':
          aValue = a.application_id || '';
          bValue = b.application_id || '';
          break;
        case 'configuration_item':
          aValue = a.configuration_item || '';
          bValue = b.configuration_item || '';
          break;
        case 'brand':
          aValue = a.brand || '';
          bValue = b.brand || '';
          break;
        case 'short_description':
          aValue = a.short_description || '';
          bValue = b.short_description || '';
          break;
        case 'caused_by_change':
          aValue = a.caused_by_change ? 1 : 0;
          bValue = b.caused_by_change ? 1 : 0;
          break;
        case 'triggering_change_request':
          const aRequests = a.triggering_change_request?.filter(req => req && req !== 'CHGXXXXXXX') || [];
          const bRequests = b.triggering_change_request?.filter(req => req && req !== 'CHGXXXXXXX') || [];
          aValue = aRequests.join(', ');
          bValue = bRequests.join(', ');
          break;
        case 'mttr':
          aValue = a.ttr_seconds ?? -1;
          bValue = b.ttr_seconds ?? -1;
          break;
        case 'mttd':
          aValue = a.ttd_seconds ?? -1;
          bValue = b.ttd_seconds ?? -1;
          break;
        case 'downtime':
          aValue = calculateIncidentDowntime(a.id) || '';
          bValue = calculateIncidentDowntime(b.id) || '';
          break;
        default:
          aValue = '';
          bValue = '';
      }

      if (bValue < aValue) {
        return order === 'desc' ? -1 : 1;
      }
      if (bValue > aValue) {
        return order === 'desc' ? 1 : -1;
      }
      return 0;
    };
  };

  // Export to Excel function
  const exportToExcel = () => {
    // Apply the same toggle filter as the table
    const dataToExport = showOnlyCausedByChange 
      ? sortedIncidents.filter(incident => incident.caused_by_change === true)
      : sortedIncidents;
    
    // Prepare data for export
    const exportData = dataToExport.map(incident => ({
      'Opened': formatDate(incident.opened),
      'Resolved': formatDate(incident.resolved),
      'ID': incident.id,
      'Priority': incident.priority,
      'Application ID': incident.application_id || 'N/A',
      'CI': incident.configuration_item,
      'Brand': incident.brand || 'N/A',
      'Short Description': incident.short_description,
      'Caused by Change': incident.caused_by_change ? 'Yes' : 'No',
      'Change Type': incident.change_type || 'N/A',
      'Triggering Change Request': (() => {
        const validRequests = incident.triggering_change_request?.filter(
          req => req && req !== 'CHGXXXXXXX'
        ) || [];
        return validRequests.length > 0 ? validRequests.join(', ') : 'N/A';
      })(),
      'MTTR': formatSeconds(incident.ttr_seconds),
      'MTTD': formatSeconds(incident.ttd_seconds),
      'Downtime': calculateIncidentDowntime(incident.id) || 'N/A'
    }));

    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'P1P2 Due to Change');

    // Generate filename with current date
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const filename = `P1P2_Incidents_${dateStr}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Calculate summaries from incident data
  const calculateSummaries = (data: IncidentData[], currentTimeRange: string, currentViewMode: string) => {
    // Calculate Brand/Month or Brand/Week summary - Group by period, then by brand
    const monthMap = new Map<string, BrandMonthSummary>();

    // Count incidents that will be processed
    const validIncidents = data.filter(incident =>
      incident.brand && incident.brand.trim() !== '' && incident.resolved
    );
    console.log('Total incidents in data:', data.length);
    console.log('Valid incidents (with brand and resolved date):', validIncidents.length);
    console.log('Filtered out incidents:', data.length - validIncidents.length);

    // Log the incidents being filtered out
    const filteredOut = data.filter(incident =>
      !incident.brand || incident.brand.trim() === '' || !incident.resolved
    );
    console.log('Incidents without brand or resolved date:', filteredOut.map(inc => ({
      id: inc.id,
      brand: inc.brand,
      resolved: inc.resolved,
      short_description: inc.short_description
    })));

    data.forEach(incident => {
      // Skip incidents with null or blank brand or resolved date
      if (!incident.brand || incident.brand.trim() === '' || !incident.resolved) {
        return;
      }

      // Use resolved date for month or week grouping based on viewMode
      const date = new Date(incident.resolved);
      let periodKey: string;
      
      if (currentViewMode === 'weekly') {
        const { week: weekNumber, year } = getISOWeekNumber(date);
        periodKey = `Week ${weekNumber} ${year}`;
      } else {
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        periodKey = `${month} ${year}`;
      }
      
      const brand = incident.brand.trim();

      // Initialize period if not exists
      if (!monthMap.has(periodKey)) {
        monthMap.set(periodKey, {
          month: periodKey,
          brands: new Map<string, BrandData>(),
          total_p1p2: 0,
          total_p1p2_change: 0,
          total_pct: '0%',
        });
      }
      
      const monthSummary = monthMap.get(periodKey)!;
      
      // Initialize brand if not exists
      if (!monthSummary.brands.has(brand)) {
        monthSummary.brands.set(brand, {
          p1p2: 0,
          p1p2_change: 0,
          pct: '0%',
        });
      }

      const brandData = monthSummary.brands.get(brand)!;
      brandData.p1p2++;
      if (incident.caused_by_change) brandData.p1p2_change++;

      monthSummary.total_p1p2++;
      if (incident.caused_by_change) monthSummary.total_p1p2_change++;
    });

    // Calculate percentages
    monthMap.forEach(monthSummary => {
      monthSummary.brands.forEach(brandData => {
        brandData.pct = brandData.p1p2 > 0
          ? `${Math.round((brandData.p1p2_change / brandData.p1p2) * 100)}%`
          : '0%';
      });
      monthSummary.total_pct = monthSummary.total_p1p2 > 0
        ? `${Math.round((monthSummary.total_p1p2_change / monthSummary.total_p1p2) * 100)}%`
        : '0%';
    });

    // Determine which periods to show based on time range filter
    // Sort by date (most recent first)
    const sortedMonths = Array.from(monthMap.keys()).sort((a, b) => {
      if (currentViewMode === 'weekly') {
        // Parse "Week X YYYY" format
        const parseWeek = (str: string) => {
          const match = str.match(/Week (\d+) (\d+)/);
          if (match) {
            return { week: parseInt(match[1]), year: parseInt(match[2]) };
          }
          return { week: 0, year: 0 };
        };
        const weekA = parseWeek(a);
        const weekB = parseWeek(b);
        if (weekA.year !== weekB.year) {
          return weekB.year - weekA.year;
        }
        return weekB.week - weekA.week;
      } else {
        // Monthly sorting
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateB.getTime() - dateA.getTime(); // Descending order (newest to oldest)
      }
    });
    
    let allMonths: BrandMonthSummary[] = sortedMonths.map(monthYear => monthMap.get(monthYear)!);
    
    setBrandMonthData(allMonths);
    setSelectedPeriods([]);

    // Calculate Impacted Portfolio summary with platform breakdown
    const impactedPortfolioMap = new Map<string, PortfolioSummary>();

    data.forEach(incident => {
      // Skip incidents with null or blank brand or resolved date
      if (!incident.brand || incident.brand.trim() === '' || !incident.resolved) {
        return;
      }

      const portfolio = incident.portfolio || 'Unknown';
      const platform = incident.platform || 'Unknown';

      if (!impactedPortfolioMap.has(portfolio)) {
        impactedPortfolioMap.set(portfolio, {
          portfolio,
          p1p2_count: 0,
          p1p2_change_count: 0,
          pct: '0%',
          platforms: new Map(),
        });
      }

      const summary = impactedPortfolioMap.get(portfolio)!;
      summary.p1p2_count++;
      if (incident.caused_by_change) summary.p1p2_change_count++;

      // Add platform breakdown
      if (!summary.platforms!.has(platform)) {
        summary.platforms!.set(platform, {
          p1p2_count: 0,
          p1p2_change_count: 0,
          pct: '0%',
        });
      }

      const platformData = summary.platforms!.get(platform)!;
      platformData.p1p2_count++;
      if (incident.caused_by_change) platformData.p1p2_change_count++;
    });

    // Calculate percentages for portfolios and platforms
    impactedPortfolioMap.forEach(summary => {
      summary.pct = summary.p1p2_count > 0
        ? `${Math.round((summary.p1p2_change_count / summary.p1p2_count) * 100)}%`
        : '0%';

      // Calculate percentages for platforms
      summary.platforms!.forEach(platformData => {
        platformData.pct = platformData.p1p2_count > 0
          ? `${Math.round((platformData.p1p2_change_count / platformData.p1p2_count) * 100)}%`
          : '0%';
      });
    });

    setImpactedPortfolioData(Array.from(impactedPortfolioMap.values()).sort((a, b) => b.p1p2_count - a.p1p2_count));

    // Calculate Caused By Portfolio summary with platform breakdown
    const causedByPortfolioMap = new Map<string, PortfolioSummary>();

    data.forEach(incident => {
      // Skip incidents with null or blank brand or resolved date
      if (!incident.brand || incident.brand.trim() === '' || !incident.resolved) {
        return;
      }

      const portfolio = incident.caused_by_portfolio || 'Unknown';
      const platform = incident.caused_by_platform || 'Unknown';

      if (!causedByPortfolioMap.has(portfolio)) {
        causedByPortfolioMap.set(portfolio, {
          portfolio,
          p1p2_count: 0,
          p1p2_change_count: 0,
          pct: '0%',
          platforms: new Map(),
        });
      }

      const summary = causedByPortfolioMap.get(portfolio)!;
      summary.p1p2_count++;
      if (incident.caused_by_change) {
        summary.p1p2_change_count++;
      }

      // Add platform breakdown
      if (!summary.platforms!.has(platform)) {
        summary.platforms!.set(platform, {
          p1p2_count: 0,
          p1p2_change_count: 0,
          pct: '0%',
        });
      }

      const platformData = summary.platforms!.get(platform)!;
      platformData.p1p2_count++;
      if (incident.caused_by_change) {
        platformData.p1p2_change_count++;
      }
    });

    // Calculate percentages - percentage of incidents due to change within each portfolio
    causedByPortfolioMap.forEach(summary => {
      summary.pct = summary.p1p2_count > 0
        ? `${Math.round((summary.p1p2_change_count / summary.p1p2_count) * 100)}%`
        : '0%';

      // Calculate percentages for platforms - percentage of incidents due to change within platform
      summary.platforms!.forEach(platformData => {
        platformData.pct = platformData.p1p2_count > 0
          ? `${Math.round((platformData.p1p2_change_count / platformData.p1p2_count) * 100)}%`
          : '0%';
      });
    });

    setCausedByPortfolioData(Array.from(causedByPortfolioMap.values()).sort((a, b) => b.p1p2_count - a.p1p2_count));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggle portfolio expansion
  const handleTogglePortfolio = (portfolio: string) => {
    setExpandedPortfolios(prev => {
      const newSet = new Set(prev);
      if (newSet.has(portfolio)) {
        newSet.delete(portfolio);
      } else {
        newSet.add(portfolio);
      }
      return newSet;
    });
  };

  // Toggle caused by portfolio expansion
  const handleToggleCausedByPortfolio = (portfolio: string) => {
    setExpandedCausedByPortfolios(prev => {
      const newSet = new Set(prev);
      if (newSet.has(portfolio)) {
        newSet.delete(portfolio);
      } else {
        newSet.add(portfolio);
      }
      return newSet;
    });
  };

  // Get unique brands from incidents
  const uniqueBrands = React.useMemo(() => {
    const brands = new Set<string>();
    incidents.forEach(incident => {
      if (incident.brand && incident.brand.trim() !== '') {
        brands.add(incident.brand);
      }
    });
    return Array.from(brands).sort();
  }, [incidents]);

  // Get unique years from resolved dates


  // Get unique portfolios from incidents
  const uniquePortfolios = React.useMemo(() => {
    const portfolios = new Set<string>();
    incidents.forEach(incident => {
      if (incident.portfolio && incident.portfolio.trim() !== '') {
        portfolios.add(incident.portfolio);
      }
    });
    return Array.from(portfolios).sort();
  }, [incidents]);

  // Get unique platforms from incidents
  const uniquePlatforms = React.useMemo(() => {
    const platforms = new Set<string>();
    incidents.forEach(incident => {
      if (incident.platform && incident.platform.trim() !== '') {
        platforms.add(incident.platform);
      }
    });
    return Array.from(platforms).sort();
  }, [incidents]);

  // Get unique caused by platforms from incidents
  const uniqueCausedByPlatforms = React.useMemo(() => {
    const platforms = new Set<string>();
    incidents.forEach(incident => {
      if (incident.caused_by_platform && incident.caused_by_platform.trim() !== '') {
        platforms.add(incident.caused_by_platform);
      }
    });
    return Array.from(platforms).sort();
  }, [incidents]);

  // Get unique caused by portfolios from incidents
  const uniqueCausedByPortfolios = React.useMemo(() => {
    const portfolios = new Set<string>();
    incidents.forEach(incident => {
      if (incident.caused_by_portfolio && incident.caused_by_portfolio.trim() !== '') {
        portfolios.add(incident.caused_by_portfolio);
      }
    });
    return Array.from(portfolios).sort();
  }, [incidents]);

  // Note: Empty array means 'All' selected (no filtering)
  // Non-empty array means specific items selected (filter to those items)

  // Filter incidents based on all selected filters
  const filteredIncidents = React.useMemo(() => {
    return incidents.filter(incident => {
      // Brand filter
      if (brandFilter.length > 0 && !brandFilter.includes(incident.brand || '')) {
        return false;
      }

      // MVC filter
      if (mvcFilter !== 'all') {
        if (mvcFilter === 'yes' && incident.mvc !== true) {
          return false;
        }
        if (mvcFilter === 'no' && incident.mvc === true) {
          return false;
        }
      }

      // Priority filter
      if (priorityFilter !== 'all') {
        if (priorityFilter === 'p1' && incident.priority !== 'P1') {
          return false;
        }
        if (priorityFilter === 'p2' && incident.priority !== 'P2') {
          return false;
        }
      }

      // Date range filter
      if (startDate || endDate) {
        if (incident.resolved) {
          const resolvedDate = new Date(incident.resolved);
          if (startDate) {
            const start = new Date(startDate);
            // Set to beginning of day to include all times on the start date
            start.setHours(0, 0, 0, 0);
            if (resolvedDate < start) {
              return false;
            }
          }
          if (endDate) {
            const end = new Date(endDate);
            // Set to end of day to include all times on the end date
            end.setHours(23, 59, 59, 999);
            if (resolvedDate > end) {
              return false;
            }
          }
        } else {
          // If date range is set but incident has no resolved date, exclude it
          return false;
        }
      }

      // Impacted Portfolio filter
      if (impactedPortfolioFilter.length > 0 && !impactedPortfolioFilter.includes(incident.portfolio || '')) {
        return false;
      }

      // Impacted Platform filter
      if (impactedPlatformFilter.length > 0 && !impactedPlatformFilter.includes(incident.platform || '')) {
        return false;
      }

      // Caused By Platform filter
      if (causedByPlatformFilter.length > 0 && !causedByPlatformFilter.includes(incident.caused_by_platform || '')) {
        return false;
      }

      // Caused By Portfolio filter
      if (causedByPortfolioFilter.length > 0 && !causedByPortfolioFilter.includes(incident.caused_by_portfolio || '')) {
        return false;
      }

      // Caused by Change toggle filter
      if (showOnlyCausedByChange && incident.caused_by_change !== true) {
        return false;
      }

      // Period filter (weeks/months selected in the brand table dropdown)
      if (selectedPeriods.length > 0 && incident.resolved) {
        const date = new Date(incident.resolved);
        let periodKey: string;
        if (viewMode === 'weekly') {
          const { week, year } = getISOWeekNumber(date);
          periodKey = `Week ${week} ${year}`;
        } else {
          const month = date.toLocaleString('en-US', { month: 'short' });
          const year = date.getFullYear();
          periodKey = `${month} ${year}`;
        }
        if (!selectedPeriods.includes(periodKey)) return false;
      }

      return true;
    });
  }, [incidents, brandFilter, mvcFilter, priorityFilter, startDate, endDate, impactedPortfolioFilter, impactedPlatformFilter, causedByPlatformFilter, causedByPortfolioFilter, showOnlyCausedByChange, selectedPeriods, viewMode]);

  // Filtered incidents for KPI cards — same filters as above but IGNORING the caused_by_change toggle
  // so the flash cards always show the true % and outage hours across all incidents
  const filteredIncidentsForKPI = React.useMemo(() => {
    return incidents.filter(incident => {
      if (brandFilter.length > 0 && !brandFilter.includes(incident.brand || '')) return false;
      if (mvcFilter !== 'all') {
        if (mvcFilter === 'yes' && incident.mvc !== true) return false;
        if (mvcFilter === 'no' && incident.mvc === true) return false;
      }
      if (priorityFilter !== 'all') {
        if (priorityFilter === 'p1' && incident.priority !== 'P1') return false;
        if (priorityFilter === 'p2' && incident.priority !== 'P2') return false;
      }
      if (startDate || endDate) {
        if (incident.resolved) {
          const resolvedDate = new Date(incident.resolved);
          if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            if (resolvedDate < start) return false;
          }
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (resolvedDate > end) return false;
          }
        } else {
          return false;
        }
      }
      if (impactedPortfolioFilter.length > 0 && !impactedPortfolioFilter.includes(incident.portfolio || '')) return false;
      if (impactedPlatformFilter.length > 0 && !impactedPlatformFilter.includes(incident.platform || '')) return false;
      if (causedByPlatformFilter.length > 0 && !causedByPlatformFilter.includes(incident.caused_by_platform || '')) return false;
      if (causedByPortfolioFilter.length > 0 && !causedByPortfolioFilter.includes(incident.caused_by_portfolio || '')) return false;
      return true;
    });
  }, [incidents, brandFilter, mvcFilter, priorityFilter, startDate, endDate, impactedPortfolioFilter, impactedPlatformFilter, causedByPlatformFilter, causedByPortfolioFilter]);

  // Period-filtered incidents for KPI cards and dashboard (applies week/month dropdown selection)
  const periodFilteredIncidentsForKPI = React.useMemo(() => {
    if (selectedPeriods.length === 0) return filteredIncidentsForKPI;
    return filteredIncidentsForKPI.filter(incident => {
      if (!incident.resolved) return false;
      const date = new Date(incident.resolved);
      let periodKey: string;
      if (viewMode === 'weekly') {
        const { week, year } = getISOWeekNumber(date);
        periodKey = `Week ${week} ${year}`;
      } else {
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        periodKey = `${month} ${year}`;
      }
      return selectedPeriods.includes(periodKey);
    });
  }, [filteredIncidentsForKPI, selectedPeriods, viewMode]);

  // Filtered incidents for summary tables (exclude incidents without brand or resolved date)
  // Uses filteredIncidentsForKPI so the toggle does NOT affect the summary tables
  // Does NOT apply period filter so brandMonthData always has all periods for the dropdown
  const filteredIncidentsForSummary = React.useMemo(() => {
    return filteredIncidentsForKPI.filter(incident =>
      incident.brand && incident.brand.trim() !== '' && incident.resolved
    );
  }, [filteredIncidentsForKPI]);

  // Log filtered incidents count
  useEffect(() => {
    console.log('Filtered incidents count:', filteredIncidents.length);
  }, [filteredIncidents]);

  // Recalculate summaries when filtered incidents change
  React.useEffect(() => {
    // Always call calculateSummaries to properly handle empty data
    calculateSummaries(filteredIncidentsForSummary, timeRange, viewMode);
  }, [filteredIncidentsForSummary, timeRange, viewMode]);

  // Recalculate portfolio tables when period selection changes
  React.useEffect(() => {
    if (selectedPeriods.length === 0) return; // calculateSummaries already handles unfiltered state
    const periodFiltered = filteredIncidentsForSummary.filter(incident => {
      if (!incident.resolved) return false;
      const date = new Date(incident.resolved);
      let periodKey: string;
      if (viewMode === 'weekly') {
        const { week, year } = getISOWeekNumber(date);
        periodKey = `Week ${week} ${year}`;
      } else {
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        periodKey = `${month} ${year}`;
      }
      return selectedPeriods.includes(periodKey);
    });

    // Recalculate impacted portfolio
    const impactedPortfolioMap = new Map<string, PortfolioSummary>();
    periodFiltered.forEach(incident => {
      if (!incident.brand || incident.brand.trim() === '' || !incident.resolved) return;
      const portfolio = incident.portfolio || 'Unknown';
      const platform = incident.platform || 'Unknown';
      if (!impactedPortfolioMap.has(portfolio)) {
        impactedPortfolioMap.set(portfolio, { portfolio, p1p2_count: 0, p1p2_change_count: 0, pct: '0%', platforms: new Map() });
      }
      const summary = impactedPortfolioMap.get(portfolio)!;
      summary.p1p2_count++;
      if (incident.caused_by_change) summary.p1p2_change_count++;
      if (!summary.platforms!.has(platform)) {
        summary.platforms!.set(platform, { p1p2_count: 0, p1p2_change_count: 0, pct: '0%' });
      }
      const platformData = summary.platforms!.get(platform)!;
      platformData.p1p2_count++;
      if (incident.caused_by_change) platformData.p1p2_change_count++;
    });
    impactedPortfolioMap.forEach(summary => {
      summary.pct = summary.p1p2_count > 0 ? `${Math.round((summary.p1p2_change_count / summary.p1p2_count) * 100)}%` : '0%';
      summary.platforms!.forEach(pd => { pd.pct = pd.p1p2_count > 0 ? `${Math.round((pd.p1p2_change_count / pd.p1p2_count) * 100)}%` : '0%'; });
    });
    setImpactedPortfolioData(Array.from(impactedPortfolioMap.values()).sort((a, b) => b.p1p2_count - a.p1p2_count));

    // Recalculate caused by portfolio
    const causedByPortfolioMap = new Map<string, PortfolioSummary>();
    periodFiltered.forEach(incident => {
      if (!incident.brand || incident.brand.trim() === '' || !incident.resolved) return;
      const portfolio = incident.caused_by_portfolio || 'Unknown';
      const platform = incident.caused_by_platform || 'Unknown';
      if (!causedByPortfolioMap.has(portfolio)) {
        causedByPortfolioMap.set(portfolio, { portfolio, p1p2_count: 0, p1p2_change_count: 0, pct: '0%', platforms: new Map() });
      }
      const summary = causedByPortfolioMap.get(portfolio)!;
      summary.p1p2_count++;
      if (incident.caused_by_change) summary.p1p2_change_count++;
      if (!summary.platforms!.has(platform)) {
        summary.platforms!.set(platform, { p1p2_count: 0, p1p2_change_count: 0, pct: '0%' });
      }
      const platformData = summary.platforms!.get(platform)!;
      platformData.p1p2_count++;
      if (incident.caused_by_change) platformData.p1p2_change_count++;
    });
    causedByPortfolioMap.forEach(summary => {
      summary.pct = summary.p1p2_count > 0 ? `${Math.round((summary.p1p2_change_count / summary.p1p2_count) * 100)}%` : '0%';
      summary.platforms!.forEach(pd => { pd.pct = pd.p1p2_count > 0 ? `${Math.round((pd.p1p2_change_count / pd.p1p2_count) * 100)}%` : '0%'; });
    });
    setCausedByPortfolioData(Array.from(causedByPortfolioMap.values()).sort((a, b) => b.p1p2_count - a.p1p2_count));
  }, [selectedPeriods, filteredIncidentsForSummary, viewMode]);

  // Calculate percentage of P1/P2 due to change (uses all filtered incidents, ignoring toggle)
  const percentageP1P2DueToChange = React.useMemo(() => {
    if (periodFilteredIncidentsForKPI.length === 0) return '0';
    const causedByChangeCount = periodFilteredIncidentsForKPI.filter(incident => incident.caused_by_change === true).length;
    const percentage = (causedByChangeCount / periodFilteredIncidentsForKPI.length) * 100;
    return percentage.toFixed(0);
  }, [periodFilteredIncidentsForKPI]);

  // Calculate average MTTD for incidents due to change
  const averageMTTDDueToChange = React.useMemo(() => {
    const incidentsDueToChange = periodFilteredIncidentsForKPI.filter(incident => incident.caused_by_change === true);
    const validMTTD = incidentsDueToChange.filter(incident => incident.ttd_seconds !== null && incident.ttd_seconds !== undefined);
    
    if (validMTTD.length === 0) return 'N/A';
    
    const totalSeconds = validMTTD.reduce((sum, incident) => sum + (incident.ttd_seconds || 0), 0);
    const avgSeconds = totalSeconds / validMTTD.length;
    
    return formatSeconds(avgSeconds);
  }, [periodFilteredIncidentsForKPI]);

  // Calculate average MTTR for incidents due to change
  const averageMTTRDueToChange = React.useMemo(() => {
    const incidentsDueToChange = periodFilteredIncidentsForKPI.filter(incident => incident.caused_by_change === true);
    const validMTTR = incidentsDueToChange.filter(incident => incident.ttr_seconds !== null && incident.ttr_seconds !== undefined);
    
    if (validMTTR.length === 0) return 'N/A';
    
    const totalSeconds = validMTTR.reduce((sum, incident) => sum + (incident.ttr_seconds || 0), 0);
    const avgSeconds = totalSeconds / validMTTR.length;
    
    return formatSeconds(avgSeconds);
  }, [periodFilteredIncidentsForKPI]);

  // Calculate total MTTD for incidents due to change
  const totalMTTDDueToChange = React.useMemo(() => {
    const incidentsDueToChange = periodFilteredIncidentsForKPI.filter(incident => incident.caused_by_change === true);
    const validMTTD = incidentsDueToChange.filter(incident => incident.ttd_seconds !== null && incident.ttd_seconds !== undefined);
    
    if (validMTTD.length === 0) return 'N/A';
    
    const totalSeconds = validMTTD.reduce((sum, incident) => sum + (incident.ttd_seconds || 0), 0);
    
    return formatSeconds(totalSeconds);
  }, [periodFilteredIncidentsForKPI]);

  // Calculate total MTTR for incidents due to change
  const totalMTTRDueToChange = React.useMemo(() => {
    const incidentsDueToChange = periodFilteredIncidentsForKPI.filter(incident => incident.caused_by_change === true);
    const validMTTR = incidentsDueToChange.filter(incident => incident.ttr_seconds !== null && incident.ttr_seconds !== undefined);
    
    if (validMTTR.length === 0) return 'N/A';
    
    const totalSeconds = validMTTR.reduce((sum, incident) => sum + (incident.ttr_seconds || 0), 0);
    
    return formatSeconds(totalSeconds);
  }, [periodFilteredIncidentsForKPI]);

  // Calculate metrics for ALL incidents (not just due to change) for the summary table
  const allIncidentsMetrics = React.useMemo(() => {
    // MTTD for all incidents
    const validMTTDAll = periodFilteredIncidentsForKPI.filter(incident => incident.ttd_seconds !== null && incident.ttd_seconds !== undefined);
    const totalMTTDAllSeconds = validMTTDAll.length > 0 
      ? validMTTDAll.reduce((sum, incident) => sum + (incident.ttd_seconds || 0), 0)
      : 0;
    const totalMTTDAll = validMTTDAll.length > 0 ? formatSeconds(totalMTTDAllSeconds) : 'N/A';
    const avgMTTDAllSeconds = validMTTDAll.length > 0 ? totalMTTDAllSeconds / validMTTDAll.length : 0;
    const avgMTTDAll = validMTTDAll.length > 0 ? formatSeconds(avgMTTDAllSeconds) : 'N/A';

    // MTTD for incidents due to change
    const incidentsDueToChange = periodFilteredIncidentsForKPI.filter(incident => incident.caused_by_change === true);
    const validMTTDChange = incidentsDueToChange.filter(incident => incident.ttd_seconds !== null && incident.ttd_seconds !== undefined);
    const totalMTTDChangeSeconds = validMTTDChange.length > 0
      ? validMTTDChange.reduce((sum, incident) => sum + (incident.ttd_seconds || 0), 0)
      : 0;
    const totalMTTDChange = validMTTDChange.length > 0 ? formatSeconds(totalMTTDChangeSeconds) : 'N/A';
    const avgMTTDChangeSeconds = validMTTDChange.length > 0 ? totalMTTDChangeSeconds / validMTTDChange.length : 0;
    const avgMTTDChange = validMTTDChange.length > 0 ? formatSeconds(avgMTTDChangeSeconds) : 'N/A';

    // Calculate percentages for MTTD
    const totalMTTDPercentage = totalMTTDAllSeconds > 0 
      ? ((totalMTTDChangeSeconds / totalMTTDAllSeconds) * 100).toFixed(1)
      : '0.0';
    const avgMTTDPercentage = avgMTTDAllSeconds > 0
      ? ((avgMTTDChangeSeconds / avgMTTDAllSeconds) * 100).toFixed(1)
      : '0.0';

    // MTTR for all incidents
    const validMTTRAll = periodFilteredIncidentsForKPI.filter(incident => incident.ttr_seconds !== null && incident.ttr_seconds !== undefined);
    const totalMTTRAllSeconds = validMTTRAll.length > 0
      ? validMTTRAll.reduce((sum, incident) => sum + (incident.ttr_seconds || 0), 0)
      : 0;
    const totalMTTRAll = validMTTRAll.length > 0 ? formatSeconds(totalMTTRAllSeconds) : 'N/A';
    const avgMTTRAllSeconds = validMTTRAll.length > 0 ? totalMTTRAllSeconds / validMTTRAll.length : 0;
    const avgMTTRAll = validMTTRAll.length > 0 ? formatSeconds(avgMTTRAllSeconds) : 'N/A';

    // MTTR for incidents due to change
    const validMTTRChange = incidentsDueToChange.filter(incident => incident.ttr_seconds !== null && incident.ttr_seconds !== undefined);
    const totalMTTRChangeSeconds = validMTTRChange.length > 0
      ? validMTTRChange.reduce((sum, incident) => sum + (incident.ttr_seconds || 0), 0)
      : 0;
    const totalMTTRChange = validMTTRChange.length > 0 ? formatSeconds(totalMTTRChangeSeconds) : 'N/A';
    const avgMTTRChangeSeconds = validMTTRChange.length > 0 ? totalMTTRChangeSeconds / validMTTRChange.length : 0;
    const avgMTTRChange = validMTTRChange.length > 0 ? formatSeconds(avgMTTRChangeSeconds) : 'N/A';

    // Calculate percentages for MTTR
    const totalMTTRPercentage = totalMTTRAllSeconds > 0
      ? ((totalMTTRChangeSeconds / totalMTTRAllSeconds) * 100).toFixed(1)
      : '0.0';
    const avgMTTRPercentage = avgMTTRAllSeconds > 0
      ? ((avgMTTRChangeSeconds / avgMTTRAllSeconds) * 100).toFixed(1)
      : '0.0';

    return {
      totalMTTDAll,
      totalMTTDChange,
      totalMTTDPercentage,
      avgMTTDAll,
      avgMTTDChange,
      avgMTTDPercentage,
      totalMTTRAll,
      totalMTTRChange,
      totalMTTRPercentage,
      avgMTTRAll,
      avgMTTRChange,
      avgMTTRPercentage
    };
  }, [periodFilteredIncidentsForKPI]);

  // Helper function to calculate duration in hours from duration string
  const calculateDurationInHours = (durationString: string): number => {
    try {
      // Duration is in format "1970-01-01 HH:MM:SS" where the time represents the duration
      const timePart = durationString.split(' ')[1]; // Get "HH:MM:SS"
      if (!timePart) return 0;

      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      return hours + (minutes / 60) + (seconds / 3600);
    } catch (error) {
      console.error('Error parsing duration:', durationString, error);
      return 0;
    }
  };

  // Calculate outage KPIs
  const outageKPIs = React.useMemo(() => {
    if (!outageData || outageData.length === 0) {
      return {
        totalP1P2OutageHours: '0h 00',
        totalP1P2OutageDueToChangeHours: '0h 00'
      };
    }

    // Get all incident IDs from ALL filtered incidents (ignoring toggle) for accurate KPI totals
    const p1p2IncidentIds = new Set(periodFilteredIncidentsForKPI.map(inc => inc.id));

    // Group outages by task_number
    const outagesByTaskNumber = new Map<string, OutageData[]>();
    outageData.forEach(outage => {
      const isP1P2Related = p1p2IncidentIds.has(outage.task_number);
      if (isP1P2Related) {
        if (!outagesByTaskNumber.has(outage.task_number)) {
          outagesByTaskNumber.set(outage.task_number, []);
        }
        outagesByTaskNumber.get(outage.task_number)!.push(outage);
      }
    });

    let totalP1P2OutageMinutes = 0;
    let totalP1P2OutageDueToChangeMinutes = 0;

    // For each task number, calculate duration from min start time to max end time
    outagesByTaskNumber.forEach((outages, taskNumber) => {
      if (outages.length === 0) return;

      // Find min start time and max end time
      const times: { start: Date; end: Date }[] = [];

      outages.forEach(outage => {
        try {
          const startTime = new Date(outage.start_time);
          const endTime = new Date(outage.end_time);
          times.push({ start: startTime, end: endTime });
        } catch (error) {
          console.error('Error parsing outage times:', outage, error);
        }
      });

      if (times.length > 0) {
        const minStartTime = new Date(Math.min(...times.map(t => t.start.getTime())));
        const maxEndTime = new Date(Math.max(...times.map(t => t.end.getTime())));

        // Calculate duration in minutes
        const durationMs = maxEndTime.getTime() - minStartTime.getTime();
        const durationMinutes = Math.floor(durationMs / (1000 * 60));

        totalP1P2OutageMinutes += durationMinutes;

        // Check if the related incident was caused by change
        const relatedIncident = periodFilteredIncidentsForKPI.find(inc => inc.id === taskNumber);
        if (relatedIncident && relatedIncident.caused_by_change) {
          totalP1P2OutageDueToChangeMinutes += durationMinutes;
        }
      }
    });

    // Format as "Xh Ym"
    const formatMinutes = (totalMinutes: number): string => {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    };

    return {
      totalP1P2OutageHours: formatMinutes(totalP1P2OutageMinutes),
      totalP1P2OutageDueToChangeHours: formatMinutes(totalP1P2OutageDueToChangeMinutes)
    };
  }, [outageData, periodFilteredIncidentsForKPI]);

  // Helper function to calculate downtime for a specific incident
  const calculateIncidentDowntime = (incidentId: string): string => {
    if (!outageData || outageData.length === 0) return '';

    // Get all outages for this incident
    const relatedOutages = outageData.filter(outage => outage.task_number === incidentId);

    if (relatedOutages.length === 0) return '';

    // Find min start time and max end time
    const times: { start: Date; end: Date }[] = [];

    relatedOutages.forEach(outage => {
      try {
        const startTime = new Date(outage.start_time);
        const endTime = new Date(outage.end_time);
        times.push({ start: startTime, end: endTime });
      } catch (error) {
        console.error('Error parsing outage times:', outage, error);
      }
    });

    if (times.length > 0) {
      const minStartTime = new Date(Math.min(...times.map(t => t.start.getTime())));
      const maxEndTime = new Date(Math.max(...times.map(t => t.end.getTime())));

      // Calculate duration in minutes
      const durationMs = maxEndTime.getTime() - minStartTime.getTime();
      const totalMinutes = Math.floor(durationMs / (1000 * 60));

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      // Format as "Xh Ym"
      return `${hours}h ${minutes}m`;
    }

    return '';
  };

  // Clear all filters
  const handleTimeRangeChange = (range: string) => {
    setTimeRange(range);
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    switch (range) {
      case 'all':
        setStartDate('');
        setEndDate('');
        break;
      case 'last7days':
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 7);
        setStartDate(last7.toISOString().split('T')[0]);
        setEndDate(todayStr);
        break;
      case 'last30days':
        const last30 = new Date(today);
        last30.setDate(today.getDate() - 30);
        setStartDate(last30.toISOString().split('T')[0]);
        setEndDate(todayStr);
        break;
      case 'last90days':
        const last90 = new Date(today);
        last90.setDate(today.getDate() - 90);
        setStartDate(last90.toISOString().split('T')[0]);
        setEndDate(todayStr);
        break;
      case 'ytd':
        setStartDate(today.getFullYear() + '-01-01');
        setEndDate(todayStr);
        break;
      case 'custom':
        // Keep current dates for custom range
        break;
    }
  };

  const clearAllFilters = () => {
    const today = new Date();
    setTimeRange('ytd');
    setStartDate(`${today.getFullYear()}-01-01`);
    setEndDate(today.toISOString().split('T')[0]);
    setMvcFilter('all');
    setPriorityFilter('all');
    setBrandFilter([]);
    setImpactedPortfolioFilter([]);
    setImpactedPlatformFilter([]);
    setCausedByPlatformFilter([]);
    setCausedByPortfolioFilter([]);
  };

  // Handle view mode change (Weekly/Monthly toggle)
  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: string | null) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'P1':
        return 'error';
      case 'P2':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Sort incidents
  const sortedIncidents = React.useMemo(() => {
    return [...filteredIncidents].sort(getComparator(order, orderBy));
  }, [filteredIncidents, order, orderBy]);

  // Filtered incidents for table only (applying the toggle)
  const sortedIncidentsForTable = React.useMemo(() => {
    if (showOnlyCausedByChange) {
      return sortedIncidents.filter(incident => incident.caused_by_change === true);
    }
    return sortedIncidents;
  }, [sortedIncidents, showOnlyCausedByChange]);

  return (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && (
        <Container maxWidth="xl">
          <Box sx={{ minHeight: '100vh', py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* %P1P2 due to change KPI */}
                <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.2,
                alignItems: 'flex-end',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                px: 3,
                py: 0.8,
                backgroundColor: '#fafafa'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#00a0dc', fontSize: '1.5rem', lineHeight: 1 }}>
                  {percentageP1P2DueToChange}%
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', lineHeight: 1, pb: 0.2 }}>
                  P1/P2 Due to Change
                </Typography>
              </Box>

              {/* Total P1/P2 Outage in Hrs KPI */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.2,
                alignItems: 'flex-end',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                px: 3,
                py: 0.8,
                backgroundColor: '#fafafa'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#f57c00', fontSize: '1.5rem', lineHeight: 1 }}>
                  {outageKPIs.totalP1P2OutageHours}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', lineHeight: 1, pb: 0.2 }}>
                  Total P1/P2 Outage
                </Typography>
              </Box>

              {/* Total P1/P2 Outage Due to Change in Hrs KPI */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.2,
                alignItems: 'flex-end',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                px: 3,
                py: 0.8,
                backgroundColor: '#fafafa'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#d32f2f', fontSize: '1.5rem', lineHeight: 1 }}>
                  {outageKPIs.totalP1P2OutageDueToChangeHours}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', lineHeight: 1, pb: 0.2 }}>
                  Total P1/P2 Outage Due to Change
                </Typography>
              </Box>
              </Box>

              {/* Trend Comparison Toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#666' }}>
                  Trend Comparison:
                </Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewChange}
                  size="small"
                  sx={{
                    '& .MuiToggleButtonGroup-grouped': {
                      border: '1px solid #d0d0d0',
                      borderRadius: '20px',
                      px: 2.5,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      '&:not(:first-of-type)': {
                        marginLeft: '8px',
                        borderLeft: '1px solid #d0d0d0',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#0066CC',
                        color: '#fff',
                        borderColor: '#0066CC',
                        '&:hover': {
                          backgroundColor: '#0052A3',
                        },
                      },
                      '&:not(.Mui-selected)': {
                        backgroundColor: '#fff',
                        color: '#333',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="weekly">
                    Weekly
                  </ToggleButton>
                  <ToggleButton value="monthly">
                    Monthly
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* All Filters in One Row */}
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={1.5} alignItems="flex-end">
                {/* Time Range */}
                <Grid item xs={12} md={1.8}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                    Time Range (Resolved date)
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={timeRange}
                      onChange={(e) => handleTimeRangeChange(e.target.value)}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                      }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="last7days">Last 7 Days</MenuItem>
                      <MenuItem value="last30days">Last 30 Days</MenuItem>
                      <MenuItem value="last90days">Last 90 Days</MenuItem>
                      <MenuItem value="ytd">Year to Date</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Custom Date Range - Show only when Custom is selected */}
                {timeRange === 'custom' && (
                  <>
                    <Grid item xs={12} md={1.3}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                        Start Date
                      </Typography>
                      <TextField
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                          backgroundColor: '#fff',
                          '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#d0d0d0' },
                            '&:hover fieldset': { borderColor: '#b0b0b0' },
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={1.3}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                        End Date
                      </Typography>
                      <TextField
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                          backgroundColor: '#fff',
                          '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#d0d0d0' },
                            '&:hover fieldset': { borderColor: '#b0b0b0' },
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Priority Dropdown */}
                <Grid item xs={12} md={1.1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                    Priority
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={priorityFilter}
                      onChange={(e) => setPriorityFilter(e.target.value)}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                      }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="p1">P1</MenuItem>
                      <MenuItem value="p2">P2</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* MVC Dropdown */}
                <Grid item xs={12} md={1.0}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                    MVC
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={mvcFilter}
                      onChange={(e) => setMvcFilter(e.target.value)}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                      }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {/* Brand */}
                <Grid item xs={12} md={1.1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                    Brand
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      multiple
                      displayEmpty
                      value={brandFilter}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        if (value.includes('all')) {
                          // Clicking 'All' always sets to empty array (All selected)
                          setBrandFilter([]);
                        } else {
                          // If currently 'All' (empty) and clicking a specific item, select only that item
                          if (brandFilter.length === 0) {
                            setBrandFilter([value[value.length - 1]]);
                          } else {
                            // Multi-select logic: add/remove items
                            setBrandFilter(value);
                          }
                        }
                      }}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                      }}
                      renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={brandFilter.length === 0} size="small" />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                      </MenuItem>
                      {uniqueBrands.map(brand => (
                        <MenuItem key={brand} value={brand}>
                          <Checkbox checked={brandFilter.length > 0 && brandFilter.indexOf(brand) > -1} size="small" />
                          <Typography variant="caption">{brand}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Impacted Portfolio */}
                <Grid item xs={12} md={1.25}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                    Impacted Portfolio
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      multiple
                      displayEmpty
                      value={impactedPortfolioFilter}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        if (value.includes('all')) {
                          setImpactedPortfolioFilter([]);
                        } else {
                          if (impactedPortfolioFilter.length === 0) {
                            setImpactedPortfolioFilter([value[value.length - 1]]);
                          } else {
                            setImpactedPortfolioFilter(value);
                          }
                        }
                      }}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                      }}
                      renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={impactedPortfolioFilter.length === 0} size="small" />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                      </MenuItem>
                      {uniquePortfolios.map(portfolio => (
                        <MenuItem key={portfolio} value={portfolio}>
                          <Checkbox checked={impactedPortfolioFilter.length > 0 && impactedPortfolioFilter.indexOf(portfolio) > -1} size="small" />
                          <Typography variant="caption">{portfolio}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Impacted Platform */}
                <Grid item xs={12} md={1.25}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                    Impacted Platform
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      multiple
                      displayEmpty
                      value={impactedPlatformFilter}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        if (value.includes('all')) {
                          setImpactedPlatformFilter([]);
                        } else {
                          if (impactedPlatformFilter.length === 0) {
                            setImpactedPlatformFilter([value[value.length - 1]]);
                          } else {
                            setImpactedPlatformFilter(value);
                          }
                        }
                      }}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                      }}
                      renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={impactedPlatformFilter.length === 0} size="small" />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                      </MenuItem>
                      {uniquePlatforms.map(platform => (
                        <MenuItem key={platform} value={platform}>
                          <Checkbox checked={impactedPlatformFilter.length > 0 && impactedPlatformFilter.indexOf(platform) > -1} size="small" />
                          <Typography variant="caption">{platform}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Caused By Platform */}
                <Grid item xs={12} md={1.25}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                    Caused By Platform
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      multiple
                      displayEmpty
                      value={causedByPlatformFilter}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        if (value.includes('all')) {
                          setCausedByPlatformFilter([]);
                        } else {
                          if (causedByPlatformFilter.length === 0) {
                            setCausedByPlatformFilter([value[value.length - 1]]);
                          } else {
                            setCausedByPlatformFilter(value);
                          }
                        }
                      }}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                      }}
                      renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={causedByPlatformFilter.length === 0} size="small" />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                      </MenuItem>
                      {uniqueCausedByPlatforms.map(platform => (
                        <MenuItem key={platform} value={platform}>
                          <Checkbox checked={causedByPlatformFilter.length > 0 && causedByPlatformFilter.indexOf(platform) > -1} size="small" />
                          <Typography variant="caption">{platform}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Caused By Portfolio */}
                <Grid item xs={12} md={1.25}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: '#666' }}>
                    Caused By Portfolio
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      multiple
                      displayEmpty
                      value={causedByPortfolioFilter}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        if (value.includes('all')) {
                          setCausedByPortfolioFilter([]);
                        } else {
                          if (causedByPortfolioFilter.length === 0) {
                            setCausedByPortfolioFilter([value[value.length - 1]]);
                          } else {
                            setCausedByPortfolioFilter(value);
                          }
                        }
                      }}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b0b0b0' },
                      }}
                      renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={causedByPortfolioFilter.length === 0} size="small" />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>All</Typography>
                      </MenuItem>
                      {uniqueCausedByPortfolios.map(portfolio => (
                        <MenuItem key={portfolio} value={portfolio}>
                          <Checkbox checked={causedByPortfolioFilter.length > 0 && causedByPortfolioFilter.indexOf(portfolio) > -1} size="small" />
                          <Typography variant="caption">{portfolio}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Clear Selection Button */}
                <Grid item xs={12} md={1.2}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3, fontSize: '0.7rem', color: 'transparent', userSelect: 'none' }}>
                    .
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={clearAllFilters}
                    sx={{
                      height: '33px',
                      fontSize: '0.7rem',
                      py: 0.5,
                      borderColor: '#d0d0d0',
                      color: '#666',
                      '&:hover': {
                        borderColor: '#b0b0b0',
                        backgroundColor: '#f5f5f5',
                      }
                    }}
                  >
                    Clear Selection
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Brand/Month Summary Table */}
            <TableContainer 
              component={Paper} 
              elevation={3} 
              sx={{ 
                mb: 4,
                ...(viewMode === 'weekly' && {
                  maxHeight: 400,
                  overflowY: 'auto',
                  overflowX: 'auto'
                })
              }}
            >
              <Table 
                size="small" 
                stickyHeader={viewMode === 'weekly'}
                sx={{ 
                  borderCollapse: 'separate', 
                  borderSpacing: 0, 
                  '& .MuiTableCell-root': { padding: '4px 8px', fontSize: '0.75rem' },
                  '& th': {
                    borderBottom: '1px solid #000',
                    borderRight: '1px solid #000',
                    borderTop: 'none',
                    borderLeft: 'none',
                  },
                  '& thead tr:first-of-type th': {
                    borderTop: '1px solid #000',
                  },
                  '& th:first-of-type': {
                    borderLeft: '1px solid #000',
                  },
                  '& td': {
                    borderBottom: '1px solid #e0e0e0',
                    borderRight: '1px solid #e0e0e0',
                    borderTop: 'none',
                    borderLeft: 'none',
                  },
                  '& td:first-of-type': {
                    borderLeft: '1px solid #e0e0e0',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      backgroundColor: '#003366', 
                      color: 'white', 
                      fontWeight: 700, 
                      textAlign: 'center', 
                      border: '1px solid #000', 
                      padding: '4px', 
                      fontSize: '0.75rem',
                      ...(viewMode === 'weekly' && {
                        position: 'sticky',
                        left: 0,
                        zIndex: 3
                      })
                    }}>
                      Brand
                    </TableCell>
                    {(() => {
                      // Get all unique brands from the data
                      const allBrands = new Set<string>();
                      brandMonthData.forEach(monthData => {
                        monthData.brands.forEach((_, brand) => allBrands.add(brand));
                      });
                      const brandArray = Array.from(allBrands).sort();

                      return (
                        <>
                          {brandArray.map(brand => (
                            <TableCell key={brand} colSpan={3} sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.75rem' }}>
                              {brand}
                            </TableCell>
                          ))}
                          <TableCell colSpan={3} sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.75rem' }}>
                            Total
                          </TableCell>
                        </>
                      );
                    })()}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ 
                      backgroundColor: '#003366', 
                      color: 'white', 
                      fontWeight: 700, 
                      textAlign: 'center', 
                      border: '1px solid #000', 
                      padding: '4px', 
                      fontSize: '0.7rem',
                      ...(viewMode === 'weekly' && {
                        position: 'sticky',
                        left: 0,
                        zIndex: 3
                      })
                    }}>
                      <Select
                        multiple
                        value={selectedPeriods}
                        onChange={(e) => {
                          const value = e.target.value as string[];
                          if (value.includes('all')) {
                            setSelectedPeriods(selectedPeriods.length === brandMonthData.length ? [] : brandMonthData.map(d => d.month));
                          } else if (value.includes('clear')) {
                            setSelectedPeriods([]);
                          } else {
                            setSelectedPeriods(value);
                          }
                        }}
                        displayEmpty
                        renderValue={() => {
                          const label = viewMode === 'weekly' ? 'Week - Year' : 'Month - Year';
                          if (selectedPeriods.length === 0) return label;
                          return `${label} (${selectedPeriods.length})`;
                        }}
                        size="small"
                        sx={{
                          color: '#000',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          minWidth: 110,
                          '.MuiSelect-icon': { color: '#000' },
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                        MenuProps={{
                          PaperProps: { sx: { maxHeight: 300 } },
                        }}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={selectedPeriods.length === brandMonthData.length && brandMonthData.length > 0} indeterminate={selectedPeriods.length > 0 && selectedPeriods.length < brandMonthData.length} size="small" />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        <MenuItem value="clear" sx={{ color: '#d32f2f' }}>
                          <ListItemText primary="Clear All" />
                        </MenuItem>
                        {brandMonthData.map(d => (
                          <MenuItem key={d.month} value={d.month}>
                            <Checkbox checked={selectedPeriods.includes(d.month)} size="small" />
                            <ListItemText primary={d.month} />
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    {(() => {
                      // Get all unique brands from the data
                      const allBrands = new Set<string>();
                      brandMonthData.forEach(monthData => {
                        monthData.brands.forEach((_, brand) => allBrands.add(brand));
                      });
                      const brandArray = Array.from(allBrands).sort();

                      return (
                        <>
                          {brandArray.map(brand => (
                            <React.Fragment key={brand}>
                              <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>#P1/P2</TableCell>
                              <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>#P1/P2 due to Change</TableCell>
                              <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>%P1/P2</TableCell>
                            </React.Fragment>
                          ))}
                          <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>#P1/P2</TableCell>
                          <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>#P1/P2 due to Change</TableCell>
                          <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>%P1/P2</TableCell>
                        </>
                      );
                    })()}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedPeriods.length > 0 ? brandMonthData.filter(d => selectedPeriods.includes(d.month)) : brandMonthData).map((monthData, index) => {
                    // Get all unique brands from all months
                    const allBrands = new Set<string>();
                    brandMonthData.forEach(md => {
                      md.brands.forEach((_, brand) => allBrands.add(brand));
                    });
                    const brandArray = Array.from(allBrands).sort();

                    return (
                      <TableRow key={index}>
                        <TableCell sx={{ 
                          border: '1px solid #e0e0e0', 
                          textAlign: 'center', 
                          fontWeight: 700, 
                          padding: '4px', 
                          fontSize: '0.75rem', 
                          color: '#003366',
                          ...(viewMode === 'weekly' && {
                            position: 'sticky',
                            left: 0,
                            backgroundColor: '#fff',
                            zIndex: 2
                          })
                        }}>
                          {monthData.month}
                        </TableCell>
                        {brandArray.map(brand => {
                          const brandData = monthData.brands.get(brand);
                          return (
                            <React.Fragment key={brand}>
                              <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>
                                {brandData?.p1p2 || 0}
                              </TableCell>
                              <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>
                                {brandData?.p1p2_change || 0}
                              </TableCell>
                              <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>
                                {brandData?.pct || '0%'}
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                        <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{monthData.total_p1p2}</TableCell>
                        <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{monthData.total_p1p2_change}</TableCell>
                        <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{monthData.total_pct}</TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total Row */}
                  {brandMonthData.length > 0 && (() => {
                    const displayData = selectedPeriods.length > 0 ? brandMonthData.filter(d => selectedPeriods.includes(d.month)) : brandMonthData;
                    const allBrands = new Set<string>();
                    brandMonthData.forEach(md => {
                      md.brands.forEach((_, brand) => allBrands.add(brand));
                    });
                    const brandArray = Array.from(allBrands).sort();

                    // Calculate totals for each brand
                    const brandTotals = new Map<string, { p1p2: number; p1p2_change: number }>();
                    brandArray.forEach(brand => {
                      brandTotals.set(brand, { p1p2: 0, p1p2_change: 0 });
                    });

                    displayData.forEach(monthData => {
                      monthData.brands.forEach((data, brand) => {
                        const total = brandTotals.get(brand)!;
                        total.p1p2 += data.p1p2;
                        total.p1p2_change += data.p1p2_change;
                      });
                    });

                    const grandTotal = {
                      p1p2: displayData.reduce((sum, md) => sum + md.total_p1p2, 0),
                      p1p2_change: displayData.reduce((sum, md) => sum + md.total_p1p2_change, 0),
                    };

                    return (
                      <TableRow>
                        <TableCell sx={{ 
                          border: '1px solid #e0e0e0', 
                          textAlign: 'center', 
                          fontWeight: 700, 
                          backgroundColor: '#f5f5f5', 
                          padding: '4px', 
                          fontSize: '0.75rem', 
                          color: '#003366',
                          ...(viewMode === 'weekly' && {
                            position: 'sticky',
                            left: 0,
                            zIndex: 2
                          })
                        }}>
                          Total
                        </TableCell>
                        {brandArray.map(brand => {
                          const total = brandTotals.get(brand)!;
                          const pct = total.p1p2 > 0 ? `${Math.round((total.p1p2_change / total.p1p2) * 100)}%` : '0%';
                          return (
                            <React.Fragment key={brand}>
                              <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                                {total.p1p2}
                              </TableCell>
                              <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                                {total.p1p2_change}
                              </TableCell>
                              <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                                {pct}
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                        <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                          {grandTotal.p1p2}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                          {grandTotal.p1p2_change}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                          {grandTotal.p1p2 > 0 ? `${Math.round((grandTotal.p1p2_change / grandTotal.p1p2) * 100)}%` : '0%'}
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>

            {/* MTTD & MTTR Summary Table */}
            <Paper elevation={1} sx={{ mb: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#bbdefb' }}>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        border: '1px solid #000',
                        color: '#000', 
                        padding: '8px' 
                      }}
                      title="Mean Time To Detect / Mean Time To Resolve"
                    >
                      MTTD
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        border: '1px solid #000',
                        color: '#000', 
                        padding: '8px' 
                      }}
                      title="Total MTTD for all incidents / Average MTTD for all incidents"
                    >
                      P1P2
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        border: '1px solid #000',
                        color: '#000', 
                        padding: '8px' 
                      }}
                      title="Total MTTD for the incidents caused due to change / Average MTTD for the incidents caused due to change"
                    >
                      P1P2 Due to Change
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        border: '1px solid #000',
                        color: '#000', 
                        padding: '8px' 
                      }}
                      title="(MTTD for the incidents caused due to change) / (MTTD for all incidents) %100"
                    >
                      %
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        border: '1px solid #000',
                        borderLeft: '1px solid #000',
                        color: '#000', 
                        padding: '8px' 
                      }}
                      title="Mean Time To Resolve"
                    >
                      MTTR
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        border: '1px solid #000',
                        color: '#000', 
                        padding: '8px' 
                      }}
                      title="Total MTTR for all incidents / Average MTTR for all incidents"
                    >
                      P1P2
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        border: '1px solid #000',
                        color: '#000', 
                        padding: '8px' 
                      }}
                      title="Total MTTR for the incidents caused due to change / Average MTTR for the incidents caused due to change"
                    >
                      P1P2 Due to Change
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem',
                        border: '1px solid #000',
                        color: '#000', 
                        padding: '8px' 
                      }}
                      title="(MTTR for the incidents caused due to change) / (MTTR for all incidents) %100"
                    >
                      %
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Total Row */}
                  <TableRow sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ 
                      fontSize: '0.75rem', 
                      border: '1px solid #000',
                      fontWeight: 600, 
                      backgroundColor: '#fafafa',
                      color: '#000',
                      padding: '8px' 
                    }}>
                      Total MTTD
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', border: '1px solid #e0e0e0', padding: '8px' }}>
                      {allIncidentsMetrics.totalMTTDAll}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', border: '1px solid #e0e0e0', padding: '8px' }}>
                      {allIncidentsMetrics.totalMTTDChange}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', border: '1px solid #e0e0e0', padding: '8px' }}>
                      {allIncidentsMetrics.totalMTTDPercentage}%
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: '0.75rem', 
                      border: '1px solid #000',
                      borderLeft: '1px solid #000',
                      fontWeight: 600, 
                      backgroundColor: '#fafafa',
                      color: '#000',
                      padding: '8px' 
                    }}>
                      Total MTTR
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', border: '1px solid #e0e0e0', padding: '8px' }}>
                      {allIncidentsMetrics.totalMTTRAll}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', border: '1px solid #e0e0e0', padding: '8px' }}>
                      {allIncidentsMetrics.totalMTTRChange}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', border: '1px solid #e0e0e0', padding: '8px' }}>
                      {allIncidentsMetrics.totalMTTRPercentage}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>

            {/* Portfolio Analysis Tables - Side by Side */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Impacted Portfolio */}
              <Grid item xs={12} md={6}>
                <TableContainer component={Paper} elevation={3} sx={{ border: '1px solid #000' }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px', fontSize: '0.75rem' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.75rem' }}>
                          Impacted Portfolio
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem', width: '30px' }}></TableCell>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>Portfolio</TableCell>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>#P1/P2</TableCell>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>#P1/P2 due to Change</TableCell>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>%P1/P2</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {impactedPortfolioData.map((row, index) => (
                        <React.Fragment key={index}>
                          {/* Portfolio Row */}
                          <TableRow sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                            <TableCell sx={{ border: '1px solid #e0e0e0', padding: '2px', textAlign: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleTogglePortfolio(row.portfolio)}
                                sx={{ padding: '2px' }}
                              >
                                {expandedPortfolios.has(row.portfolio) ? <KeyboardArrowDown sx={{ fontSize: '1rem' }} /> : <KeyboardArrowRight sx={{ fontSize: '1rem' }} />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px 8px', fontWeight: 700, color: '#003366' }}>{row.portfolio}</TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{row.p1p2_count}</TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{row.p1p2_change_count}</TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{row.pct}</TableCell>
                          </TableRow>
                          {/* Platform Breakdown Rows */}
                          {expandedPortfolios.has(row.portfolio) && row.platforms && (
                            <>
                              {Array.from(row.platforms.entries()).map(([platform, platformData]) => (
                                <TableRow key={`${row.portfolio}-${platform}`} sx={{ backgroundColor: '#f9fafb' }}>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', padding: '2px' }}></TableCell>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.7rem', padding: '4px 8px 4px 24px', fontStyle: 'italic', color: '#666' }}>
                                    {platform}
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.7rem', color: '#666' }}>
                                    {platformData.p1p2_count}
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.7rem', color: '#666' }}>
                                    {platformData.p1p2_change_count}
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.7rem', color: '#666' }}>
                                    {platformData.pct}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                      {impactedPortfolioData.length > 0 && (
                        <TableRow>
                          <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}></TableCell>
                          <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px 8px', fontSize: '0.75rem', color: '#003366' }}>Total</TableCell>
                          <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center', backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                            {impactedPortfolioData.reduce((sum, row) => sum + row.p1p2_count, 0)}
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center', backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                            {impactedPortfolioData.reduce((sum, row) => sum + row.p1p2_change_count, 0)}
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center', backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                            {impactedPortfolioData.length > 0
                              ? `${Math.round((impactedPortfolioData.reduce((sum, row) => sum + row.p1p2_change_count, 0) /
                                impactedPortfolioData.reduce((sum, row) => sum + row.p1p2_count, 0)) * 100)}%`
                              : '0%'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* Caused By Portfolio */}
              <Grid item xs={12} md={6}>
                <TableContainer component={Paper} elevation={3} sx={{ border: '1px solid #000' }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px', fontSize: '0.75rem' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.75rem' }}>
                          Caused By Portfolio
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem', width: '30px' }}></TableCell>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>Portfolio</TableCell>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>#P1/P2</TableCell>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>#P1/P2 due to Change</TableCell>
                        <TableCell sx={{ backgroundColor: '#003366', color: 'white', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>%P1/P2</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {causedByPortfolioData.map((row, index) => (
                        <React.Fragment key={index}>
                          {/* Portfolio Row */}
                          <TableRow sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                            <TableCell sx={{ border: '1px solid #e0e0e0', padding: '2px', textAlign: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleCausedByPortfolio(row.portfolio)}
                                sx={{ padding: '2px' }}
                              >
                                {expandedCausedByPortfolios.has(row.portfolio) ? <KeyboardArrowDown sx={{ fontSize: '1rem' }} /> : <KeyboardArrowRight sx={{ fontSize: '1rem' }} />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px 8px', fontWeight: 700, color: '#003366' }}>{row.portfolio}</TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{row.p1p2_count}</TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{row.p1p2_change_count}</TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.75rem' }}>{row.pct}</TableCell>
                          </TableRow>
                          {/* Platform Breakdown Rows */}
                          {expandedCausedByPortfolios.has(row.portfolio) && row.platforms && (
                            <>
                              {Array.from(row.platforms.entries()).map(([platform, platformData]) => (
                                <TableRow key={`${row.portfolio}-${platform}`} sx={{ backgroundColor: '#f9fafb' }}>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', padding: '2px' }}></TableCell>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.7rem', padding: '4px 8px 4px 24px', fontStyle: 'italic', color: '#666' }}>
                                    {platform}
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.7rem', color: '#666' }}>
                                    {platformData.p1p2_count}
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.7rem', color: '#666' }}>
                                    {platformData.p1p2_change_count}
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', padding: '4px', fontSize: '0.7rem', color: '#666' }}>
                                    {platformData.pct}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                      {causedByPortfolioData.length > 0 && (() => {
                        const totalP1P2 = causedByPortfolioData.reduce((sum, row) => sum + row.p1p2_count, 0);
                        const totalP1P2Change = causedByPortfolioData.reduce((sum, row) => sum + row.p1p2_change_count, 0);
                        const totalPct = totalP1P2 > 0 ? `${Math.round((totalP1P2Change / totalP1P2) * 100)}%` : '0%';

                        return (
                          <TableRow>
                            <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}></TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, backgroundColor: '#f5f5f5', padding: '4px 8px', fontSize: '0.75rem', color: '#003366' }}>Total</TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center', backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                              {totalP1P2}
                            </TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center', backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                              {totalP1P2Change}
                            </TableCell>
                            <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center', backgroundColor: '#f5f5f5', padding: '4px', fontSize: '0.75rem' }}>
                              {totalPct}
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>

      {/* P1/P2 Due to Change Data - Detailed Incident Table */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
          Incident Details
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Caused by Change Toggle Switch */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'center',
            px: 2,
            py: 1,
          }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
              All Incidents
            </Typography>
            <Switch
              checked={showOnlyCausedByChange}
              onChange={(e) => setShowOnlyCausedByChange(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#00a0dc',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#00a0dc',
                },
              }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
              Caused by Change Only
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
            sx={{
              backgroundColor: '#00a0dc',
              color: 'white',
              '&:hover': {
                backgroundColor: '#0086ba',
              },
            }}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper} elevation={3} sx={{ border: '1px solid #000' }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px', fontSize: '0.75rem' } }}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={14} sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.75rem' }}>
                P1/P2 Incidents
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'opened'}
                  direction={orderBy === 'opened' ? order : 'asc'}
                  onClick={() => handleRequestSort('opened')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Opened
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'resolved'}
                  direction={orderBy === 'resolved' ? order : 'asc'}
                  onClick={() => handleRequestSort('resolved')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Resolved
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'priority'}
                  direction={orderBy === 'priority' ? order : 'asc'}
                  onClick={() => handleRequestSort('priority')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Priority
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'application_id'}
                  direction={orderBy === 'application_id' ? order : 'asc'}
                  onClick={() => handleRequestSort('application_id')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Application ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'configuration_item'}
                  direction={orderBy === 'configuration_item' ? order : 'asc'}
                  onClick={() => handleRequestSort('configuration_item')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  CI
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'brand'}
                  direction={orderBy === 'brand' ? order : 'asc'}
                  onClick={() => handleRequestSort('brand')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Brand
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'short_description'}
                  direction={orderBy === 'short_description' ? order : 'asc'}
                  onClick={() => handleRequestSort('short_description')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Short Description
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'caused_by_change'}
                  direction={orderBy === 'caused_by_change' ? order : 'asc'}
                  onClick={() => handleRequestSort('caused_by_change')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Caused by Change
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'change_type'}
                  direction={orderBy === 'change_type' ? order : 'asc'}
                  onClick={() => handleRequestSort('change_type')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Change Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'triggering_change_request'}
                  direction={orderBy === 'triggering_change_request' ? order : 'asc'}
                  onClick={() => handleRequestSort('triggering_change_request')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Triggering Change Request
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'mttr'}
                  direction={orderBy === 'mttr' ? order : 'asc'}
                  onClick={() => handleRequestSort('mttr')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  MTTR
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'mttd'}
                  direction={orderBy === 'mttd' ? order : 'asc'}
                  onClick={() => handleRequestSort('mttd')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  MTTD
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#003366', color: 'black', fontWeight: 700, textAlign: 'center', border: '1px solid #000', padding: '4px', fontSize: '0.7rem' }}>
                <TableSortLabel
                  active={orderBy === 'downtime'}
                  direction={orderBy === 'downtime' ? order : 'asc'}
                  onClick={() => handleRequestSort('downtime')}
                  sx={{
                    color: 'black !important',
                    '&:hover': { color: 'black !important' },
                    '& .MuiTableSortLabel-icon': { color: 'black !important' },
                    '&.Mui-active': { color: 'black !important' },
                    '&.Mui-active .MuiTableSortLabel-icon': { color: 'black !important' },
                  }}
                >
                  Downtime
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedIncidentsForTable.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No incidents found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedIncidentsForTable
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((incident) => (
                  <TableRow
                    key={incident.id}
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                  >
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px' }}>{formatDate(incident.opened)}</TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px' }}>{formatDate(incident.resolved)}</TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px' }}>
                      <a 
                        href={`https://maersk.service-now.com/now/nav/ui/classic/params/target/incident.do%3Fsysparm_query%3Dnumber%3D${incident.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#00a0dc', textDecoration: 'none' }}
                      >
                        {incident.id}
                      </a>
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', padding: '4px' }}>
                      <Chip
                        label={incident.priority}
                        color={getPriorityColor(incident.priority)}
                        size="small"
                        sx={{ fontWeight: 600, height: 18, fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px' }}>{incident.application_id || 'N/A'}</TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px' }}>{incident.configuration_item}</TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px' }}>{incident.brand || 'N/A'}</TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', maxWidth: 300, padding: '4px' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {incident.short_description}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontSize: '0.75rem', padding: '4px' }}>
                      {incident.caused_by_change ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px' }}>
                      {incident.change_type || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '0.75rem', padding: '4px' }}>
                      {(() => {
                        const validRequests = incident.triggering_change_request?.filter(
                          req => req && req !== 'CHGXXXXXXX'
                        ) || [];
                        return validRequests.length > 0 ? validRequests.join(', ') : 'N/A';
                      })()}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontSize: '0.75rem', padding: '4px' }}>
                      {formatSeconds(incident.ttr_seconds)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontSize: '0.75rem', padding: '4px' }}>
                      {formatSeconds(incident.ttd_seconds)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontSize: '0.75rem', padding: '4px' }}>
                      {calculateIncidentDowntime(incident.id) || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, { label: 'All', value: -1 }]}
          component="div"
          count={sortedIncidentsForTable.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      </Box>
    </Container>
      )}
    </>
  );
};

export default P1P2ChangeIncidents;
